import {
    Box,
    Flex,
    FormControl,
    Link,
    Spinner,
    Stack,
    Text,
    useOutsideClick,
    VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import usePlacesService from 'react-google-autocomplete/lib/usePlacesAutocompleteService';

import { FormLabel, Input, Select } from '@/components/v4';
import secret from '@/secret.json';
import { useQuery } from 'hooks/useCustomQuery';
import getAddressess from '@/API/Addresses/getAddressess';
import { getCountriesList } from '@/API/Country/getCountriesList';
import { PlatformEnums } from '@/utils/platforms';
import {
    descriptionStyling,
    formLabelStyling,
    stackSpacing,
    stackStyling,
} from 'components/forms/new/formStyles';
import {
    buildAddressDisplay,
    buildGooglePlaceDisplay,
    mapGooglePlaceToForm,
    mapAddressToForm,
} from './addressHelpers';

/**
 * Address selection field for the 8x8 service flows. Lets the user pick a saved
 * 8x8 platform address, search via Google Places, or enter an address manually.
 * Auto-populates the company's existing 8x8 platform address on load.
 *
 * Reports its current selection to the parent through `onChange` with:
 *   { effectiveAddress, existingAddressID, addressFilled, countries }
 * The parent uses these (plus addressHelpers) to build the /address payload.
 *
 * @param {object}   props
 * @param {boolean}  props.darkMode
 * @param {string}   props.currentCompany - company id used to load addresses
 * @param {Function} props.onChange
 */
export default function EightByEightAddressPicker({
    darkMode,
    currentCompany,
    onChange,
}) {
    const [showManual, setShowManual] = useState(false);
    // ID of an existing 8x8 platform address, if one was found for the company.
    const [existingAddressID, setExistingAddressID] = useState(null);

    const [addressDisplay, setAddressDisplay] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const pickerRef = useRef(null);
    useOutsideClick({ ref: pickerRef, handler: () => setIsPickerOpen(false) });
    const [parsedAddress, setParsedAddress] = useState({
        street1: '',
        streetNumber: '',
        streetName: '',
        city: '',
        postCode: '',
        stateOrProvince: '',
        country: '',
    });
    const [manualAddress, setManualAddress] = useState({
        street1: '',
        city: '',
        postCode: '',
        stateOrProvince: '',
        country: '',
    });

    // Countries list — used to resolve the country name to a countryID GUID,
    // which the /address endpoint requires (the country name alone is rejected).
    const { data: countries = [] } = useQuery({
        queryKey: ['countriesList'],
        queryFn: async () => {
            const res = await getCountriesList();
            return res.data || [];
        },
    });

    // Options for the manual-entry Country dropdown (value = country name, kept
    // consistent with resolveCountryID which maps the name back to a GUID).
    const countryOptions = useMemo(
        () => countries.map((c) => ({ value: c.name, label: c.name })),
        [countries],
    );

    // Existing addresses for the company — used to find an 8x8 platform address.
    const { data: addresses = [] } = useQuery({
        queryKey: ['addresses', currentCompany],
        queryFn: () => getAddressess(currentCompany),
        enabled: Boolean(currentCompany),
    });

    // All addresses already linked to the 8x8 platform (pinned in the picker;
    // the first is populated on load).
    const eightByEightAddresses = useMemo(
        () =>
            addresses.filter((addr) =>
                addr?.platformAddresses?.some(
                    (pa) => pa.platformID === PlatformEnums.EIGHTBYEIGHT,
                ),
            ),
        [addresses],
    );

    // #2457: Company addresses not yet linked to 8x8 — shown so the user can
    // pick an existing TCAP address rather than always creating a new one.
    const otherAddresses = useMemo(() => {
        const eightByEightIDs = new Set(eightByEightAddresses.map((a) => a.id));
        return addresses.filter((addr) => !eightByEightIDs.has(addr.id));
    }, [addresses, eightByEightAddresses]);

    const {
        placesService,
        placePredictions,
        getPlacePredictions,
        isPlacePredictionsLoading,
    } = usePlacesService({
        apiKey: secret.googleAutocomplete.testKey,
        debounce: 300,
    });

    // Parses a Google PlaceResult into the form's address shape and marks it as
    // a new (to-be-created) address.
    const applyPlaceDetails = (place, fallbackDisplay = '') => {
        const mapped = mapGooglePlaceToForm(place, fallbackDisplay);
        const display = buildGooglePlaceDisplay(place, fallbackDisplay, mapped);
        setExistingAddressID(null);
        setParsedAddress(mapped);
        setSearchValue(display);
        setAddressDisplay(display);
    };

    // Selects a saved 8x8 platform address — reuse its ID and skip creating one.
    const selectSavedAddress = (addr) => {
        const mapped = mapAddressToForm(addr);
        const display = buildAddressDisplay(addr, mapped);
        setExistingAddressID(addr.id ?? null);
        setParsedAddress(mapped);
        setSearchValue(display);
        setAddressDisplay(display);
        setIsPickerOpen(false);
    };

    // Selects a Google prediction — fetch full details, then parse.
    const selectPrediction = (prediction) => {
        if (!placesService) return;
        placesService.getDetails({ placeId: prediction.place_id }, (place) => {
            if (place) applyPlaceDetails(place, prediction.description);
            setIsPickerOpen(false);
        });
    };

    // Populate the first saved 8x8 address on load.
    useEffect(() => {
        const first = eightByEightAddresses[0];
        if (!first) return;
        const mapped = mapAddressToForm(first);
        setExistingAddressID(first.id ?? null);
        setParsedAddress(mapped);
        const display = buildAddressDisplay(first, mapped);
        setAddressDisplay(display);
        setSearchValue(display);
    }, [eightByEightAddresses]);

    // Saved 8x8 addresses filtered by the current search text (all when empty),
    // so they stay pinned above the Google predictions.
    const filteredSaved = useMemo(() => {
        if (!searchValue) return eightByEightAddresses;
        const q = searchValue.toLowerCase();
        return eightByEightAddresses.filter((addr) =>
            buildAddressDisplay(addr, mapAddressToForm(addr))
                .toLowerCase()
                .includes(q),
        );
    }, [eightByEightAddresses, searchValue]);

    // #2457: Other company addresses filtered by search text.
    const filteredOther = useMemo(() => {
        if (!searchValue) return otherAddresses;
        const q = searchValue.toLowerCase();
        return otherAddresses.filter((addr) =>
            buildAddressDisplay(addr, mapAddressToForm(addr))
                .toLowerCase()
                .includes(q),
        );
    }, [otherAddresses, searchValue]);

    const addressFilled = showManual
        ? Boolean(
              manualAddress.street1 &&
                  manualAddress.city &&
                  manualAddress.postCode &&
                  manualAddress.country,
          )
        : Boolean(addressDisplay);

    // Report the current selection to the parent so it can build the payload.
    useEffect(() => {
        onChange?.({
            effectiveAddress: showManual ? manualAddress : parsedAddress,
            existingAddressID,
            addressFilled,
            isManual: showManual,
            countries,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showManual,
        manualAddress,
        parsedAddress,
        existingAddressID,
        addressFilled,
        countries,
    ]);

    return (
        <FormControl sx={{ margin: '0 !important' }}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Address <span style={{ color: '#e53e3e' }}>*</span>
                    </FormLabel>
                    <Text sx={descriptionStyling} maxWidth="200px">
                        Start typing to search for an address.
                    </Text>
                </Box>
                <Box flex="1" minWidth="450px" sx={{ paddingTop: '15px' }}>
                    {!showManual ? (
                        <VStack align="flex-start" spacing={1} width="100%">
                            <Box
                                ref={pickerRef}
                                position="relative"
                                width="100%">
                                <Input
                                    value={searchValue}
                                    placeholder="Start typing to search for an address..."
                                    onFocus={() => setIsPickerOpen(true)}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setSearchValue(v);
                                        // Editing invalidates a prior selection.
                                        setAddressDisplay('');
                                        setExistingAddressID(null);
                                        setIsPickerOpen(true);
                                        if (v)
                                            getPlacePredictions({ input: v });
                                    }}
                                />
                                {isPickerOpen &&
                                    (filteredSaved.length > 0 ||
                                        filteredOther.length > 0 ||
                                        (searchValue &&
                                            placePredictions.length > 0) ||
                                        isPlacePredictionsLoading) && (
                                        <Box
                                            position="absolute"
                                            zIndex={20}
                                            mt={1}
                                            width="100%"
                                            maxHeight="260px"
                                            overflowY="auto"
                                            bg={darkMode ? 'gray.800' : 'white'}
                                            border="1px solid"
                                            borderColor={
                                                darkMode
                                                    ? 'dark.tcap-borderGray'
                                                    : 'gray.200'
                                            }
                                            borderRadius="md"
                                            boxShadow="md">
                                            {filteredSaved.length > 0 && (
                                                <>
                                                    <Text
                                                        px={3}
                                                        py={1}
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                        color={
                                                            darkMode
                                                                ? 'gray.300'
                                                                : 'gray.500'
                                                        }>
                                                        Saved 8×8 addresses
                                                    </Text>
                                                    {filteredSaved.map(
                                                        (addr) => (
                                                            <Box
                                                                key={addr.id}
                                                                px={3}
                                                                py={2}
                                                                cursor="pointer"
                                                                _hover={{
                                                                    bg: darkMode
                                                                        ? 'gray.700'
                                                                        : 'gray.100',
                                                                }}
                                                                onClick={() =>
                                                                    selectSavedAddress(
                                                                        addr,
                                                                    )
                                                                }>
                                                                <Text fontSize="sm">
                                                                    {buildAddressDisplay(
                                                                        addr,
                                                                        mapAddressToForm(
                                                                            addr,
                                                                        ),
                                                                    )}
                                                                </Text>
                                                            </Box>
                                                        ),
                                                    )}
                                                </>
                                            )}
                                            {/* #2457: company addresses not yet linked to 8x8 */}
                                            {filteredOther.length > 0 && (
                                                <>
                                                    <Text
                                                        px={3}
                                                        py={1}
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                        color={
                                                            darkMode
                                                                ? 'gray.300'
                                                                : 'gray.500'
                                                        }>
                                                        Company addresses
                                                    </Text>
                                                    {filteredOther.map(
                                                        (addr) => (
                                                            <Box
                                                                key={addr.id}
                                                                px={3}
                                                                py={2}
                                                                cursor="pointer"
                                                                _hover={{
                                                                    bg: darkMode
                                                                        ? 'gray.700'
                                                                        : 'gray.100',
                                                                }}
                                                                onClick={() =>
                                                                    selectSavedAddress(
                                                                        addr,
                                                                    )
                                                                }>
                                                                <Text fontSize="sm">
                                                                    {buildAddressDisplay(
                                                                        addr,
                                                                        mapAddressToForm(
                                                                            addr,
                                                                        ),
                                                                    )}
                                                                </Text>
                                                            </Box>
                                                        ),
                                                    )}
                                                </>
                                            )}
                                            {searchValue &&
                                                placePredictions.length > 0 && (
                                                    <>
                                                        <Text
                                                            px={3}
                                                            py={1}
                                                            fontSize="xs"
                                                            fontWeight="bold"
                                                            color={
                                                                darkMode
                                                                    ? 'gray.300'
                                                                    : 'gray.500'
                                                            }>
                                                            Search results
                                                        </Text>
                                                        {placePredictions.map(
                                                            (p) => (
                                                                <Box
                                                                    key={
                                                                        p.place_id
                                                                    }
                                                                    px={3}
                                                                    py={2}
                                                                    cursor="pointer"
                                                                    _hover={{
                                                                        bg: darkMode
                                                                            ? 'gray.700'
                                                                            : 'gray.100',
                                                                    }}
                                                                    onClick={() =>
                                                                        selectPrediction(
                                                                            p,
                                                                        )
                                                                    }>
                                                                    <Text fontSize="sm">
                                                                        {
                                                                            p.description
                                                                        }
                                                                    </Text>
                                                                </Box>
                                                            ),
                                                        )}
                                                    </>
                                                )}
                                            {isPlacePredictionsLoading && (
                                                <Flex
                                                    px={3}
                                                    py={2}
                                                    align="center"
                                                    gap={2}>
                                                    <Spinner size="xs" />
                                                    <Text fontSize="sm">
                                                        Searching…
                                                    </Text>
                                                </Flex>
                                            )}
                                        </Box>
                                    )}
                            </Box>
                            <Link
                                color="messenger.500"
                                fontSize="sm"
                                cursor="pointer"
                                onClick={() => {
                                    // Carry the current search selection into
                                    // the manual fields for editing.
                                    setManualAddress({
                                        street1: parsedAddress.street1 || '',
                                        city: parsedAddress.city || '',
                                        postCode: parsedAddress.postCode || '',
                                        stateOrProvince:
                                            parsedAddress.stateOrProvince || '',
                                        country: parsedAddress.country || '',
                                    });
                                    setShowManual(true);
                                }}>
                                Can&apos;t find your address? Enter manually
                            </Link>
                        </VStack>
                    ) : (
                        <VStack align="flex-start" spacing={2}>
                            <Input
                                placeholder="Street"
                                value={manualAddress.street1}
                                onChange={(e) =>
                                    setManualAddress((p) => ({
                                        ...p,
                                        street1: e.target.value,
                                    }))
                                }
                            />
                            <Input
                                placeholder="City"
                                value={manualAddress.city}
                                onChange={(e) =>
                                    setManualAddress((p) => ({
                                        ...p,
                                        city: e.target.value,
                                    }))
                                }
                            />
                            <Input
                                placeholder="Postcode"
                                value={manualAddress.postCode}
                                onChange={(e) =>
                                    setManualAddress((p) => ({
                                        ...p,
                                        postCode: e.target.value,
                                    }))
                                }
                            />
                            <Input
                                placeholder="State / Province"
                                value={manualAddress.stateOrProvince}
                                onChange={(e) =>
                                    setManualAddress((p) => ({
                                        ...p,
                                        stateOrProvince: e.target.value,
                                    }))
                                }
                            />
                            <Select
                                placeholder="Select Country"
                                value={manualAddress.country}
                                options={countryOptions}
                                onChange={(e) =>
                                    setManualAddress((p) => ({
                                        ...p,
                                        country: e.target.value,
                                    }))
                                }
                            />
                            <Link
                                color="messenger.500"
                                fontSize="sm"
                                cursor="pointer"
                                onClick={() => {
                                    setShowManual(false);
                                }}>
                                Use address search instead
                            </Link>
                        </VStack>
                    )}
                </Box>
            </Stack>
        </FormControl>
    );
}
